<template>
  <admin-wrapper
    ref="send-message-wrapper"
    :straight-title="additionalModuleName"
    :inputs="inputs"
    :has-buttons="true"
    :module-enabler="moduleEnabler"
    has-validation
    :is-custom-saving="isSaving"
    @save="save"
  >
    <template v-for="input in inputs" #[`${input.name}.input`]>
      <v-col cols="8" :key="input.name">
        <data-input v-model="params[input.name]" v-bind="input" v-on="input.listeners" />
      </v-col>
    </template>
  </admin-wrapper>
</template>

<script>
import { get, call } from 'vuex-pathify';

import AdminWrapper from '@/components/AdminWrapper';
import DataInput from '@/components/DataInput';

export default {
  name: 'SendMessageSettings',
  components: {
    AdminWrapper,
    DataInput,
  },
  data: () => ({
    params: {
      send_message_sms_cc_enabled: false,
      send_message_sms_phone_numbers: [],
      send_message_email_cc_enabled: false,
      send_message_email_addresses: [],
    },
    isSaving: false,
  }),
  computed: {
    additionalModuleName: get('admin/additionalModules.SEND_MESSAGE_MODULE.verbose_name'),
    current_send_message_sms_cc_enabled: get('admin/settingsValues@send_message_sms_cc_enabled'),
    current_send_message_email_cc_enabled: get('admin/settingsValues@send_message_email_cc_enabled'),
    current_send_message_email_addresses: get('admin/settingsValues@send_message_email_addresses'),
    current_send_message_sms_phone_numbers: get('admin/settingsValues@send_message_sms_phone_numbers'),
    moduleEnabler() {
      return {
        name: 'send_message_module_enabled',
        straightText: this.additionalModuleName,
        subtitle: this.$i18n.t('admin.moduleEnablerSubtitle', { suffix: this.additionalModuleName }),
      };
    },
    inputs() {
      return [
        {
          name: 'send_message_sms_cc_enabled',
          translationPath: 'admin.sendMessage.sendSMSCopies',
          dataType: { name: 'switch' },
          align: 'start',
        },
        {
          name: 'send_message_sms_phone_numbers',
          translationPath: 'admin.sendMessage.phones',
          subtitle: 'admin.sendMessage.phonesSubtitle',
          align: 'start',
          dataType: {
            name: 'combobox',
          },
          disabled: !this.params['send_message_sms_cc_enabled'],
          appendIcon: '',
          multiple: true,
          outlined: true,
          deletableChips: true,
          dense: true,
          chips: true,
          listeners: {
            drop: e => this.handleNoDrop(e),
            dragend: e => this.handleNoDrop(e),
            dragenter: e => this.handleNoDrop(e),
          },
          rules: [v => (v ? v?.every(tx => !this.$_validatePhone(tx)) : true) || 'rules.invalidPhoneNumbers'],
        },
        {
          name: 'send_message_email_cc_enabled',
          translationPath: 'admin.sendMessage.sendEmailCopies',
          dataType: { name: 'switch' },
          align: 'start',
        },
        {
          name: 'send_message_email_addresses',
          translationPath: 'admin.sendMessage.emails',
          subtitle: 'admin.sendMessage.emailsSubtitle',
          align: 'start',
          dataType: {
            name: 'combobox',
          },
          disabled: !this.params['send_message_email_cc_enabled'],
          appendIcon: '',
          multiple: true,
          outlined: true,
          deletableChips: true,
          dense: true,
          chips: true,
          listeners: {
            drop: e => this.handleNoDrop(e),
            dragend: e => this.handleNoDrop(e),
            dragenter: e => this.handleNoDrop(e),
          },
        },
      ];
    },
  },
  methods: {
    editSingleSetting: call('admin/editSingleSetting'),
    handleNoDrop(e) {
      e.preventDefault();
      return false;
    },
    init() {
      this.inputs.forEach(input => {
        this.params[input.name] = this[`current_${input.name}`];
      });
      this.isFetched = true;
    },
    async save() {
      this.isSaving = true;
      try {
        await Promise.all(
          this.inputs.map(input => {
            if (this.params[input.name] === this[`current_${input.name}`]) {
              return;
            }
            return this.editSingleSetting({
              key: input.name,
              body: {
                value: this.params[input.name],
              },
            });
          })
        );
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.successEditSetting'),
        });
      } catch (error) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.settingChangeFailed'),
        });
        console.log(error);
      } finally {
        this.isSaving = false;
        this.init();
      }
    },
  },
  mounted() {
    this.init();
  },
};
</script>

<style scoped lang="scss">
::v-deep {
  .v-autocomplete:not(.v-input--is-focused).v-select--chips input {
    max-height: unset !important;
  }
  .v-select__selections {
    flex-wrap: wrap !important;
    gap: 4px;
    .v-chip {
      margin: 0 !important;
    }
  }
}
</style>
